import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
    ArrowLeft,
    Users,
    Layers,
    BookOpen,
    Mail,
    Phone,
    School,
    Hash,
    GraduationCap,
    RefreshCcw,
    LinkIcon,
    Calendar,
} from "lucide-react";
import { getTeacherClasses } from "../../utils/admin";

const SectionHeader = ({ children, accent = "from-[#2148C0] to-blue-400" }) => (
    <h3 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center mb-4">
        <div className={`w-2 h-7 md:h-8 bg-gradient-to-b ${accent} rounded-full mr-3`} />
        {children}
    </h3>
);

const TeacherClassesPage = () => {
    const { teacherId } = useParams();
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("token");
            const res = await getTeacherClasses(teacherId, token);
            if (!res?.success) {
                setError(res?.message || "Failed to load teacher.");
                return;
            }
            setPayload(res);
        } catch (err) {
            console.error(err);
            setError("Failed to load teacher.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [teacherId]);

    if (loading) {
        return (
            <div>
                <BackLink />
                <div className="h-44 bg-white border border-gray-100 animate-pulse rounded-2xl mb-6" />
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <div
                            key={i}
                            className="h-32 bg-white border border-gray-100 animate-pulse rounded-2xl"
                        />
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div>
                <BackLink />
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
                    {error}
                </div>
            </div>
        );
    }

    const { teacher, counts, groups, yearGroups, classSelections, students } = payload;
    // Backend now sends a merged `classes` array; fall back to building it
    // locally so older API responses still work.
    const classes =
        payload.classes ||
        [
            ...(yearGroups || []).map((y) => ({
                kind: "yearGroup",
                id: `year:${y.label}`,
                name: y.label,
                description: "Year group",
                studentCount: y.count,
            })),
            ...(groups || []).map((g) => ({
                kind: "group",
                id: `group:${g.group_id}`,
                name: g.groupName,
                description: g.description || "Teacher-created class",
                studentCount: g.memberCount,
                group_id: g.group_id,
            })),
        ];
    const totalClasses = counts.classes ?? classes.length;

    return (
        <div>
            <BackLink />

            {/* Hero (teacher profile) */}
            <div className="mb-8 bg-gradient-to-r from-[#2148C0] to-blue-600 rounded-2xl p-6 md:p-8 text-white relative overflow-hidden shadow-lg shadow-[#2148C0]/20">
                <div className="absolute -top-20 -right-20 w-48 h-48 bg-white/10 rounded-full" />
                <div className="absolute -bottom-16 -left-16 w-32 h-32 bg-white/5 rounded-full" />
                <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <Avatar t={teacher} />
                        <div>
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 border border-white/20 text-white text-xs font-medium mb-2">
                                <GraduationCap size={12} /> Teacher
                            </div>
                            <p className="text-2xl md:text-3xl font-bold">
                                {teacher.firstName} {teacher.lastName}
                            </p>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-blue-100 mt-1">
                                {teacher.school && (
                                    <span className="inline-flex items-center gap-1.5">
                                        <School size={13} /> {teacher.school}
                                    </span>
                                )}
                                {teacher.email && (
                                    <span className="inline-flex items-center gap-1.5">
                                        <Mail size={13} /> {teacher.email}
                                    </span>
                                )}
                                {teacher.phoneNumber && (
                                    <span className="inline-flex items-center gap-1.5">
                                        <Phone size={13} /> {teacher.phoneNumber}
                                    </span>
                                )}
                                <span className="inline-flex items-center gap-1.5">
                                    <Hash size={13} /> ID {teacher.user_id}
                                </span>
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={load}
                        className="self-start inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-[#2148C0] hover:bg-blue-50 text-sm font-semibold shadow"
                    >
                        <RefreshCcw size={15} /> Refresh
                    </button>
                </div>
            </div>

            {/* Counts */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                <Stat
                    icon={Users}
                    label="Students"
                    value={counts.students}
                    color="bg-blue-50 text-[#2148C0]"
                />
                <Stat
                    icon={Layers}
                    label="Classes"
                    value={totalClasses}
                    color="bg-green-50 text-green-600"
                />
                <Stat
                    icon={BookOpen}
                    label="Year groups"
                    value={yearGroups.length}
                    color="bg-yellow-50 text-yellow-600"
                />
            </div>

            {/* Classes (yearGroups + teacher-owned groups) */}
            <section className="mb-10">
                <SectionHeader accent="from-green-500 to-blue-500">
                    Classes ({totalClasses})
                </SectionHeader>
                {classes.length === 0 ? (
                    <EmptyState text="No students yet, so no classes to show. Once students join this teacher (by code or email), their year-groups appear here as classes." />
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                        {classes.map((c) => (
                            <ClassCard key={c.id} c={c} />
                        ))}
                    </div>
                )}
            </section>

            {/* Class selections (JSS1..SS3 subscriptions) */}
            {classSelections && classSelections.length > 0 && (
                <section className="mb-10">
                    <SectionHeader accent="from-purple-500 to-blue-500">
                        Class selections
                    </SectionHeader>
                    <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5">
                        <p className="text-xs text-gray-500 mb-3">
                            Classes these students enrolled in on the app.
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {classSelections.map((c) => (
                                <span
                                    key={c.className}
                                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-50 border border-green-100 text-green-700 text-sm font-semibold"
                                >
                                    {c.className}
                                    <span className="px-2 py-0.5 rounded-full bg-white text-xs">
                                        {c.count}
                                    </span>
                                </span>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Students */}
            <section>
                <SectionHeader accent="from-[#2148C0] to-purple-500">
                    Students ({students.length})
                </SectionHeader>
                {students.length === 0 ? (
                    <EmptyState text="No students linked to this teacher yet." />
                ) : (
                    <div className="rounded-2xl bg-white border border-gray-100 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-blue-50/60 text-left">
                                    <tr className="text-xs uppercase tracking-[0.14em] text-[#2148C0]">
                                        <th className="px-5 py-3 font-semibold">Student</th>
                                        <th className="px-5 py-3 font-semibold">Student ID</th>
                                        <th className="px-5 py-3 font-semibold">School</th>
                                        <th className="px-5 py-3 font-semibold">Year group</th>
                                        <th className="px-5 py-3 font-semibold">Link</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {students.map((s) => (
                                        <tr
                                            key={s.user_id}
                                            className="border-t border-gray-100 hover:bg-blue-50/40"
                                        >
                                            <td className="px-5 py-3">
                                                <div className="flex items-center gap-3">
                                                    <Avatar t={s} small />
                                                    <div>
                                                        <p className="font-semibold text-gray-900">
                                                            {s.firstName} {s.lastName}
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            {s.email || "no email"}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-5 py-3 text-gray-700 font-mono text-xs">
                                                {s.student_id || "—"}
                                            </td>
                                            <td className="px-5 py-3 text-gray-700">
                                                {s.school || "—"}
                                            </td>
                                            <td className="px-5 py-3 text-gray-700">
                                                {s.yearGroup || "—"}
                                            </td>
                                            <td className="px-5 py-3">
                                                {s.linkType === "code" ? (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                                        <LinkIcon size={12} /> Via Code
                                                    </span>
                                                ) : s.linkType === "email" ? (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                                                        <Mail size={12} /> Via Email
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                                        Unknown
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
};

const ClassCard = ({ c }) => {
    const isYearGroup = c.kind === "yearGroup";
    const Icon = isYearGroup ? Calendar : Layers;
    const tone = isYearGroup
        ? {
            icon: "bg-blue-50 text-[#2148C0]",
            badge: "bg-blue-50 text-[#2148C0]",
            kindLabel: "Year group",
            kindChip: "bg-blue-50 text-[#2148C0] border-blue-100",
        }
        : {
            icon: "bg-purple-50 text-purple-600",
            badge: "bg-purple-50 text-purple-700",
            kindLabel: "Group",
            kindChip: "bg-purple-50 text-purple-700 border-purple-100",
        };
    return (
        <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all">
            <div className="flex items-start gap-3">
                <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${tone.icon}`}>
                    <Icon size={18} />
                </span>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <p className="text-base font-bold text-gray-900 truncate">
                            {c.name}
                        </p>
                        <span className={`text-[10px] uppercase tracking-[0.14em] font-semibold px-2 py-0.5 rounded-full border ${tone.kindChip}`}>
                            {tone.kindLabel}
                        </span>
                    </div>
                    {c.description && (
                        <p className="text-xs text-gray-500 line-clamp-2">
                            {c.description}
                        </p>
                    )}
                </div>
            </div>
            <div className="mt-4 flex items-center justify-between">
                <span className="text-xs text-gray-500">Students</span>
                <span className={`text-sm font-bold ${tone.badge} px-2.5 py-0.5 rounded-full`}>
                    {(c.studentCount || 0).toLocaleString()}
                </span>
            </div>
        </div>
    );
};

const BackLink = () => (
    <Link
        to="/teachers"
        className="inline-flex items-center gap-2 text-sm text-[#2148C0] hover:text-blue-800 mb-6 font-medium"
    >
        <ArrowLeft size={16} /> Back to teachers
    </Link>
);

const Stat = ({ icon: Icon, label, value, color }) => (
    <div className="flex items-center gap-4 rounded-2xl bg-white border border-gray-100 p-5 shadow-sm">
        <span className={`flex h-12 w-12 items-center justify-center rounded-xl ${color}`}>
            <Icon size={20} />
        </span>
        <div>
            <p className="text-xs uppercase tracking-[0.16em] text-gray-500 font-semibold">
                {label}
            </p>
            <p className="text-3xl font-bold text-gray-900 mt-0.5">
                {Number(value || 0).toLocaleString()}
            </p>
        </div>
    </div>
);

const EmptyState = ({ text }) => (
    <div className="rounded-2xl border border-dashed border-gray-300 bg-white py-10 text-center text-gray-500 text-sm">
        {text}
    </div>
);

const Avatar = ({ t, small }) => {
    const initials = `${(t.firstName || "?").charAt(0)}${(t.lastName || "").charAt(0)}`.toUpperCase();
    const size = small ? "h-9 w-9 text-xs" : "h-16 w-16 text-xl";
    if (t.profile_image) {
        return (
            <img
                src={t.profile_image}
                alt={initials}
                className={`${size} rounded-xl object-cover border border-white/40`}
                onError={(e) => {
                    e.target.style.display = "none";
                }}
            />
        );
    }
    return (
        <div
            className={`${size} rounded-xl bg-white/15 backdrop-blur-sm border border-white/30 text-white flex items-center justify-center font-bold ${small ? "bg-gradient-to-br from-[#2148C0] to-blue-500 text-white border-transparent" : ""}`}
        >
            {initials}
        </div>
    );
};

export default TeacherClassesPage;
